<template>
    <div class="s-media-gallery" :class="{ light: theme === 'light' }">
        <div class="media-items">
            <transition-group name="fade-in">
                <template v-for="(item, i) in gallery">
                    <s-media
                        v-if="i === activeIndex"
                        :key="item.name"
                        v-bind="item.media"
                    />
                </template>
            </transition-group>
        </div>
        <div class="navigation-wrap">
            <div
                ref="nav"
                class="navigation"
                :class="navigationClassName"
                :style="navigationStyle"
            >
                <div class="nav-transformer">
                    <div ref="indicator" class="nav-indicator" />
                    <div ref="nav-items" class="nav-items">
                        <div
                            v-for="(item, i) in gallery"
                            :key="item.name"
                            class="nav-item"
                            :class="{ active: i === activeIndex }"
                            @click="activeIndex = i"
                        >
                            <span>{{ item.name }}</span>
                        </div>
                    </div>
                </div>
            </div>
            <button
                class="arrow left"
                :class="{ hidden: !leftArrowVisible }"
                @click="buttonLeftClick"
            >
                <a-glyph name="i-chevron-big-left--16" />
            </button>
            <button
                class="arrow right"
                :class="{ hidden: !rightArrowVisible }"
                @click="buttonRightClick"
            >
                <a-glyph name="i-chevron-big-right--16" />
            </button>
        </div>
    </div>
</template>

<script>
import AGlyph from '@core/components/glyph/glyph.vue';
import SMedia from '@core/components/media/media.vue';

export default {
    name: 'SMediaGallery',

    components: {
        AGlyph,
        SMedia,
    },

    props: {
        gallery: {
            type: Array,
            default: () => [],
        },

        theme: {
            type: String,
            validator: (prop) => ['light', 'dark'].includes(prop),
            default: 'dark',
        },
    },

    data: () => ({
        leftArrowVisible: false,
        rightArrowVisible: false,
        activeIndex: 0,
        navOffset: 0,
    }),

    computed: {
        navigationClassName() {
            return {
                'with-left-arrow': this.leftArrowVisible,
                'with-right-arrow': this.rightArrowVisible,
            };
        },

        navigationStyle() {
            return {
                '--nav-offset': `${this.navOffset}px`,
            };
        },
    },

    watch: {
        activeIndex() {
            this.stateChange();
        },
    },

    mounted() {
        window.addEventListener('resize', this.stateChange);
        this.$refs.indicator.addEventListener('transitionend', this.checkArrowState);
        this.stateChange();
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.stateChange);
        this.$refs.indicator.removeEventListener('transitionend', this.checkArrowState);
    },

    methods: {
        stateChange() {
            this.moveIndicator();
            this.moveNavigation();
            this.checkArrowState();
        },

        moveIndicator() {
            const indicator = this.$refs.indicator;
            const activeElement = this.$refs['nav-items'].children[this.activeIndex];

            const left = activeElement.offsetLeft;
            const width = activeElement.offsetWidth;

            indicator.style.left = `${left}px`;
            indicator.style.width = `${width}px`;
        },

        checkArrowState() {
            this.leftArrowVisible = false;
            this.rightArrowVisible = false;

            const parentRect = this.$refs.nav.getBoundingClientRect();
            const children = this.$refs['nav-items'].children;

            for (let i = 0; i < children.length; i++) {
                const childRect = children[i].getBoundingClientRect();

                if (childRect.left < parentRect.left) {
                    this.leftArrowVisible = true;
                } else if (childRect.right > parentRect.right) {
                    this.rightArrowVisible = true;
                }
            }
        },

        // moves active element to the center of navigation if possible
        moveNavigation() {
            const PADDING = 6;
            const activeElement = this.$refs['nav-items'].children[this.activeIndex];
            const containerWidth = this.$refs.nav.offsetWidth;
            const itemsWidth = this.$refs['nav-items'].offsetWidth;
            const activeElementWidth = activeElement.offsetWidth;
            const activeElementLeft = activeElement.offsetLeft;
            const maxOffset = containerWidth - itemsWidth - PADDING * 2;
            let offset = containerWidth / 2 - activeElementWidth / 2 - activeElementLeft - PADDING;
            offset = Math.min(0, offset);
            offset = Math.max(maxOffset, offset);

            this.navOffset = offset;
        },

        buttonLeftClick() {
            this.activeIndex = Math.max(0, this.activeIndex - 1);
        },

        buttonRightClick() {
            this.activeIndex = Math.min(this.gallery.length - 1, this.activeIndex + 1);
        },
    },
};
</script>

<style lang="postcss" scoped>
.s-media-gallery {
    --nav-offset: 0px;
    --fade-offset: 30px;
    --fade-width: 24px;
    width: 100%;

    @media (--viewport-desktop) {
        --fade-offset: 40px;
        --fade-width: 24px;
    }

    &:deep(.a-video-card) {
        aspect-ratio: unset;
    }

    &:deep(.a-video-card__wrapper),
    &:deep(.a-video-card__img),
    &:deep(.a-video-card .a-picture__img) {
        position: relative;
    }

    &.light {
        .navigation-wrap {
            background: rgba(213, 226, 246, 0.50);
        }
        .navigation {
            color: var(--av-brand-primary);
        }
    }
}

.media-items {
    position: relative;
    width: 100%;
}

.navigation-wrap {
    max-width: 100%;
    margin-top: 40px;
    position: relative;
    background: rgba(52, 90, 147, 0.5);
    backdrop-filter: blur(8px);
    border-radius: 32px;
    width: fit-content;
    margin-inline: auto;
    overflow: hidden;
}

.navigation {
    @mixin caption-accent;
    position: relative;
    white-space: nowrap;
    height: 32px;
    padding: 6px;
    color: var(--av-inversed-primary);

    &.with-left-arrow {
        mask-image: linear-gradient(
            90deg,
            transparent 0,
            transparent var(--fade-offset),
            var(--av-inversed-primary) calc(var(--fade-offset) + var(--fade-width))
        );
    }

    &.with-right-arrow {
        mask-image: linear-gradient(
            90deg,
            var(--av-inversed-primary) calc(100% - var(--fade-offset) - var(--fade-width)),
            transparent calc(100% - var(--fade-offset)),
            transparent
        );
    }

    &.with-left-arrow.with-right-arrow {
        mask-image: linear-gradient(
            90deg,
            transparent 0,
            transparent var(--fade-offset),
            var(--av-inversed-primary) calc(var(--fade-offset) + var(--fade-width)),
            var(--av-inversed-primary) calc(100% - var(--fade-offset) - var(--fade-width)),
            transparent calc(100% - var(--fade-offset)),
            transparent
        );
    }

    @media (--viewport-desktop) {
        @mixin body-accent;
        height: 52px;
    }
}

.nav-items {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    width: 100%;
    min-width: fit-content;
    height: 100%;
}

.nav-item {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    flex-shrink: 0;
    height: 20px;
    cursor: pointer;
    border-radius: 24px;
    transition: color 320ms ease-out;

    @media (--viewport-desktop) {
        height: 40px;
    }

    &.active {
        color: var(--av-nav-primary);
    }
}

.nav-transformer {
    transform: translateX(var(--nav-offset));
    transition-property: transform;
    transition-duration: 320ms;
    transition-timing-function: ease-out;
}

.nav-indicator {
    z-index: -1;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 0;
    background: var(--av-inversed-primary);
    border-radius: 24px;
    transition-property: left, width;
    transition-duration: 320ms;
    transition-timing-function: ease-out;
}

.arrow {
    position: absolute;
    top: 0;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &.left {
        inset-inline-start: 0;
        padding-inline: 10px 8px;

        @media (--viewport-desktop) {
            padding-inline: 16px 10px;
        }
    }

    &.right {
        inset-inline-end: 0;
        padding-inline: 8px 10px;

        @media (--viewport-desktop) {
            padding-inline: 10px 16px;
        }
    }

    &.hidden {
        display: none;
    }

    .a-glyph {
        fill: var(--av-inversed-primary);
    }
}

.fade-in-enter-active,
.fade-in-leave-active {
    transition: opacity 320ms ease;
}

.fade-in-enter-active {
    top: 0;
    position: absolute;
    width: 100%;
    height: 100%;
}

.fade-in-enter,
.fade-in-leave-to {
    opacity: 0;
}
</style>
